import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Commission {
  id: string
  sales_rep: string
  deal_name: string
  deal_value: number
  commission_rate: number
  commission_amount: number
  status: string
  pay_date: string
}

interface Payout {
  id: string
  sales_rep: string
  period: string
  base_commission: number
  bonus: number
  spiffs: number
  total_payout: number
  deals_count: number
  quota_attainment: number
}

interface CommissionTier {
  id: string
  name: string
  quota_threshold: number
  commission_rate: number
  bonus_multiplier: number
  reps_in_tier: number
  avg_performance: number
}

interface Statistics {
  total_commissions_paid: number
  pending_commissions: number
  avg_commission_rate: number
  top_earner_amount: number
  quota_attainment_avg: number
  payout_accuracy: number
}

export default function CommissionTrackingPage() {
  const [commissions, setCommissions] = useState<Commission[]>([])
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [tiers, setTiers] = useState<CommissionTier[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [commRes, payoutsRes, tiersRes, statsRes] = await Promise.all([
        fetch('/api/commissiontracking/commissions'),
        fetch('/api/commissiontracking/payouts'),
        fetch('/api/commissiontracking/tiers'),
        fetch('/api/commissiontracking/statistics')
      ])
      if (commRes.ok) setCommissions(await commRes.json())
      if (payoutsRes.ok) setPayouts(await payoutsRes.json())
      if (tiersRes.ok) setTiers(await tiersRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching commission tracking data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'paid') return 'bg-green-100 text-green-800'
    if (status === 'approved') return 'bg-blue-100 text-blue-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Commission Tracking</h1>
          <p className="mt-2 text-gray-600">Phase 235 - Commissions, payouts, and tiers</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Paid</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_commissions_paid)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(statistics.pending_commissions)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_commission_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Earner</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.top_earner_amount)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Quota Avg</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.quota_attainment_avg}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Accuracy</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.payout_accuracy}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Commissions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rep</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {commissions.slice(0, 8).map((comm) => (
                  <tr key={comm.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{comm.sales_rep}</td>
                    <td className="px-6 py-4 text-gray-500">{comm.deal_name}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(comm.deal_value)}</td>
                    <td className="px-6 py-4 text-blue-600">{comm.commission_rate}%</td>
                    <td className="px-6 py-4 font-bold text-green-600">{formatCurrency(comm.commission_amount)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(comm.status)}`}>{comm.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{comm.pay_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Payout Summary</h2>
            </div>
            <div className="p-6 space-y-4">
              {payouts.map((payout) => (
                <div key={payout.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{payout.sales_rep}</span>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(payout.total_payout)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{payout.period} | {payout.deals_count} deals | {payout.quota_attainment}% quota</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Base</p>
                      <p className="font-medium">{formatCurrency(payout.base_commission)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Bonus</p>
                      <p className="font-medium text-blue-600">{formatCurrency(payout.bonus)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">SPIFFs</p>
                      <p className="font-medium text-purple-600">{formatCurrency(payout.spiffs)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Commission Tiers</h2>
            </div>
            <div className="p-6 space-y-4">
              {tiers.map((tier) => (
                <div key={tier.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{tier.name}</span>
                    <span className="text-lg font-bold text-blue-600">{tier.commission_rate}%</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Threshold</p>
                      <p className="font-medium">{tier.quota_threshold}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Multiplier</p>
                      <p className="font-medium text-purple-600">{tier.bonus_multiplier}x</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Reps</p>
                      <p className="font-medium text-green-600">{tier.reps_in_tier}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
